import React from 'react';
import arePropsEqual from 'react-fast-compare';
import PropTypes from 'prop-types';
import { Icon } from '@modulor/react';
import Local from '../../Local/Local';
import NavigationItem, {
  NavLinkWithAnalytics,
} from '../NavigationItem/NavigationItem';
import { getDataTestAttributes } from '../../../utils/testing-locators';
import { NAVIGATION, NAVIGATION_LINK } from '../../../constants/dataTestIds';
import { useAnalyticsScreenName } from '../../../hooks/useAnalyticsScreenName';
import { ANALYTICS_SCREEN_AREAS } from '../../../constants/analytics';
import styles from './NavigationBlock.module.css';
import { BONUSES, TOURNAMENTS } from '../../../constants/routeTemplates';
import BonusNavigationItem from '../NavigationItem/BonusNavigationItem';
import { useIsUnifiedPromoBonusSystemEnabled } from '../../../hooks/useIsUnifiedPromoBonusSystemEnabled';
import TournamentNavigationItem from '../NavigationItem/TournamentNavigationItem';

const NavigationBlock = ({
  title,
  titleEventAction,
  navItems,
  sectionIndex,
  namespace,
  showAllLink,
  eventItemFieldName,
  analyticsDataItem,
  analyticsDataAll,
}) => {
  const isUnifiedBonusSystemEnabled = useIsUnifiedPromoBonusSystemEnabled();
  const screenName = useAnalyticsScreenName();

  const updatedAnalyticsDataAll = {
    ...analyticsDataAll,
    screen_name: screenName,
    screen_area: ANALYTICS_SCREEN_AREAS.SIDEBAR,
    section_index: sectionIndex,
  };

  return (
    <div
      className={styles['lc-NavigationBlock']}
      {...getDataTestAttributes(NAVIGATION)}
    >
      {title && (
        <div className={styles['lc-title']}>
          {title}
          {showAllLink ? (
            <NavLinkWithAnalytics
              className={styles.all}
              to={showAllLink}
              dataLayerEventData={{
                eventAction: titleEventAction,
              }}
              analyticsData={updatedAnalyticsDataAll}
            >
              <span>
                <Local>All</Local>
              </span>
              <Icon name="chevron_right" className={styles.chevron} />
            </NavLinkWithAnalytics>
          ) : null}
        </div>
      )}

      <div className={styles['lc-list']}>
        {navItems?.map?.(
          (
            {
              translationKey,
              IconComponent,
              eventAction,
              link,
              count,
              id,
              analyticAreaDetail,
            },
            index
          ) => {
            const analyticsData = {
              ...analyticsDataItem,
              screen_name: screenName,
              screen_area: ANALYTICS_SCREEN_AREAS.SIDEBAR,
              section_index: sectionIndex,
              screen_area_detail: analyticAreaDetail,
              position_index: index,
              ...(eventItemFieldName ? { [eventItemFieldName]: id } : {}),
            };
            if (analyticsData.category_id) {
              analyticsData.event_detail = analyticsData.category_id;
              delete analyticsData.category_id;
            }

            const componentMap = {
              [BONUSES]: isUnifiedBonusSystemEnabled
                ? BonusNavigationItem
                : NavigationItem,
              [TOURNAMENTS]: TournamentNavigationItem,
            };

            const NavigationItemComponent =
              componentMap[link] || NavigationItem;

            return (
              <NavigationItemComponent
                key={translationKey}
                to={link}
                count={count}
                icon={<IconComponent />}
                dataLayerEventData={{
                  eventAction: eventAction,
                  position_index: index,
                  section_index: sectionIndex,
                  ...(eventItemFieldName ? { [eventItemFieldName]: id } : {}),
                }}
                analyticsData={analyticsData}
                {...getDataTestAttributes(NAVIGATION_LINK)}
              >
                <Local namespace={namespace}>{translationKey}</Local>
              </NavigationItemComponent>
            );
          }
        ) ?? null}
      </div>
    </div>
  );
};

NavigationBlock.propTypes = {
  title: PropTypes.node,
  navItems: PropTypes.arrayOf(
    PropTypes.shape({
      IconComponent: PropTypes.any,
      eventAction: PropTypes.string,
      link: PropTypes.string,
      translationKey: PropTypes.string,
    })
  ).isRequired,
  sectionIndex: PropTypes.number,
  namespace: PropTypes.string.isRequired,
};
NavigationBlock.defaultProps = {
  sectionIndex: 0,
  title: null,
};

export default React.memo(NavigationBlock, arePropsEqual);
